import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useMemo } from 'react'
import { ScorecardMutators } from '~/mutators/scorecards'
import { useTRPC } from '~/services/trpc'

import { AnalyticsQueryFilter } from '@hap/analytics'
import { Heading2 } from '@hap/components'

import { ChannelScopingSection } from './-components/channel-scoping-section'
import { DirectionSelector, DirectionValue } from './-components/direction-selector'
import { RecordingDurationSection } from './-components/recording-duration-section'
import { SpeakerRatioSection } from './-components/speaker-ratio-section'
import { VoicemailFilterSection } from './-components/voicemail-filter-section'
import { WorkflowScopingSection } from './-components/workflow-scoping-section'

export const Route = createFileRoute(
  '/_authenticated/$alias/_app/scorecard/$scorecardId/edit/index2',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const trpc = useTRPC()
  const params = Route.useParams()

  const { data: scorecard } = useQuery(
    trpc.scorecards.getById.queryOptions(params.scorecardId),
  )
  return !scorecard ? null : <ScopingSection scorecard={scorecard} />
}

function ScopingSection({ scorecard }: { scorecard: any }) {
  const trpc = useTRPC()
  const { update } = ScorecardMutators.useUpdate()
  // Get workspace channel IDs for pre-filtering
  const { data: workspace } = useQuery(
    trpc.workspaces.getById.queryOptions(
      { workspaceId: scorecard.workspaceId! },
      { enabled: !!scorecard.workspaceId },
    ),
  )

  // Parse existing filters into UI state
  const scopingState = useMemo(
    () => parseFilters(scorecard.filters || []),
    [scorecard.filters],
  )

  // Helper to update filters on the server
  const updateFilters = useCallback(
    async (newState: Partial<ScopingState>) => {
      const merged = { ...scopingState, ...newState }
      const filters = buildFilters(merged)
      await update({ scorecardId: scorecard.id, values: { filters } })
    },
    [scopingState, scorecard.id, update],
  )

  // Direction change handler
  const handleDirectionChange = useCallback(
    (direction: DirectionValue) => {
      updateFilters({ direction })
    },
    [updateFilters],
  )

  // Channel handlers
  const handleAddChannel = useCallback(
    (channelId: string) => {
      if (!scopingState.channelIds.includes(channelId)) {
        updateFilters({ channelIds: [...scopingState.channelIds, channelId] })
      }
    },
    [scopingState.channelIds, updateFilters],
  )

  const handleRemoveChannel = useCallback(
    (channelId: string) => {
      updateFilters({
        channelIds: scopingState.channelIds.filter((id) => id !== channelId),
      })
    },
    [scopingState.channelIds, updateFilters],
  )

  // Workflow handlers
  const handleAddWorkflow = useCallback(
    (workflowId: string) => {
      if (!scopingState.workflowIds.includes(workflowId)) {
        updateFilters({ workflowIds: [...scopingState.workflowIds, workflowId] })
      }
    },
    [scopingState.workflowIds, updateFilters],
  )

  const handleRemoveWorkflow = useCallback(
    (workflowId: string) => {
      updateFilters({
        workflowIds: scopingState.workflowIds.filter((id) => id !== workflowId),
      })
    },
    [scopingState.workflowIds, updateFilters],
  )

  // Recording duration handlers
  const handleDurationMinChange = useCallback(
    (seconds: number) => {
      updateFilters({ recordingDurationMin: seconds })
    },
    [updateFilters],
  )

  const handleDurationMaxChange = useCallback(
    (seconds: number | null) => {
      updateFilters({ recordingDurationMax: seconds })
    },
    [updateFilters],
  )

  // Speaker ratio handlers
  const handleSpeakerRatioEnabledChange = useCallback(
    (enabled: boolean) => {
      updateFilters({ speakerRatioEnabled: enabled })
    },
    [updateFilters],
  )

  const handleSpeakerRatioLimitChange = useCallback(
    (limit: number) => {
      updateFilters({ speakerRatioLimit: limit })
    },
    [updateFilters],
  )

  // Voicemail handler
  const handleVoicemailChange = useCallback(
    (enabled: boolean) => {
      updateFilters({ excludeVoicemails: enabled })
    },
    [updateFilters],
  )

  return (
    <div className="h-full space-y-8 overflow-y-auto p-6">
      <div>
        <Heading2>Scoping</Heading2>
        <p className="text-muted-foreground mt-1">
          Define which interactions this scorecard applies to.
        </p>
      </div>

      <DirectionSelector
        value={scopingState.direction}
        onChange={handleDirectionChange}
      />

      <ChannelScopingSection
        channelIds={scopingState.channelIds}
        workspaceChannelIds={workspace?.channelIds || []}
        hasWorkspace={!!scorecard.workspaceId}
        onAdd={handleAddChannel}
        onRemove={handleRemoveChannel}
      />

      <WorkflowScopingSection
        workflowIds={scopingState.workflowIds}
        onAdd={handleAddWorkflow}
        onRemove={handleRemoveWorkflow}
      />

      <RecordingDurationSection
        minValue={scopingState.recordingDurationMin}
        maxValue={scopingState.recordingDurationMax}
        onMinChange={handleDurationMinChange}
        onMaxChange={handleDurationMaxChange}
      />

      <SpeakerRatioSection
        enabled={scopingState.speakerRatioEnabled}
        limit={scopingState.speakerRatioLimit}
        onEnabledChange={handleSpeakerRatioEnabledChange}
        onLimitChange={handleSpeakerRatioLimitChange}
      />

      <VoicemailFilterSection
        enabled={scopingState.excludeVoicemails}
        onChange={handleVoicemailChange}
      />
    </div>
  )
}

interface ScopingState {
  direction: DirectionValue
  channelIds: string[]
  workflowIds: string[]
  recordingDurationMin: number
  recordingDurationMax: number | null
  speakerRatioEnabled: boolean
  speakerRatioLimit: number
  excludeVoicemails: boolean
}

function parseFilters(filters: AnalyticsQueryFilter[]): ScopingState {
  let direction: DirectionValue = 'any'
  const channelIds: string[] = []
  const workflowIds: string[] = []
  let recordingDurationMin = 0
  let recordingDurationMax: number | null = null
  let speakerRatioEnabled = false
  let speakerRatioLimit = 75
  let excludeVoicemails = false

  for (const f of filters) {
    if (f.field === 'isOutbound' && 'value' in f && f.op === 'eq') {
      direction = f.value === true ? 'outbound' : 'inbound'
    }
    if (f.field === 'channelId' && 'values' in f && f.op === 'in') {
      channelIds.push(...(f.values as string[]))
    }
    if (f.field === 'workflowId' && 'values' in f && f.op === 'in') {
      workflowIds.push(...(f.values as string[]))
    }
    if (f.field === 'recordingDuration' && 'value' in f && f.op === 'gte') {
      recordingDurationMin = parseInt(String(f.value), 10) || 0
    }
    if (f.field === 'recordingDuration' && 'value' in f && f.op === 'lte') {
      recordingDurationMax = parseInt(String(f.value), 10) || null
    }
    if (
      (f as any).field === 'speakerRatioLimit' &&
      'value' in f &&
      (f as any).op === 'eq'
    ) {
      speakerRatioEnabled = true
      speakerRatioLimit = parseInt(String(f.value), 10) || 75
    }
    if (f.field === 'isVoicemail' && 'value' in f && f.op === 'eq' && f.value === false) {
      excludeVoicemails = true
    }
  }

  return {
    direction,
    channelIds,
    workflowIds,
    recordingDurationMin,
    recordingDurationMax,
    speakerRatioEnabled,
    speakerRatioLimit,
    excludeVoicemails,
  }
}

function buildFilters(state: ScopingState): AnalyticsQueryFilter[] {
  const filters: AnalyticsQueryFilter[] = []

  if (state.direction !== 'any') {
    filters.push({
      field: 'isOutbound',
      op: 'eq',
      value: state.direction === 'outbound',
    })
  }

  if (state.channelIds.length > 0) {
    filters.push({
      field: 'channelId',
      op: 'in',
      values: state.channelIds,
    })
  }

  if (state.workflowIds.length > 0) {
    filters.push({
      field: 'workflowId',
      op: 'in',
      values: state.workflowIds,
    })
  }

  if (state.recordingDurationMin > 0) {
    filters.push({
      field: 'recordingDuration',
      op: 'gte',
      value: String(state.recordingDurationMin),
    })
  }

  if (state.recordingDurationMax !== null && state.recordingDurationMax > 0) {
    filters.push({
      field: 'recordingDuration',
      op: 'lte',
      value: String(state.recordingDurationMax),
    })
  }

  if (state.speakerRatioEnabled) {
    filters.push({
      field: 'speakerRatioLimit' as any,
      op: 'eq',
      value: String(state.speakerRatioLimit),
    })
  }

  if (state.excludeVoicemails) {
    filters.push({
      field: 'isVoicemail',
      op: 'eq',
      value: false,
    })
  }

  return filters
}
